import {TActionType} from '../Actions/Constants/ActionType';
import InputType, {TInputType} from '../Actions/Constants/InputType';
import FocusableEventType, {TFocusableEventType} from '../Events/Constants/FocusableEventType';
import FocusableEvent from '../Events/FocusableEvent';
import IEventDetailFocusChange from '../Events/Interfaces/IEventDetailFocusChange';
import IEventDetailFocusWillChange from '../Events/Interfaces/IEventDetailFocusWillChangeDetail';
import * as Errors from '../Shared/Constants/Errors';
import EventTarget from '../Shared/Util/EventTarget';
import isArrayWithLength from '../Shared/Util/isArrayWithLength';
import throttle from '../Shared/Util/throttle';
import FocusableNodeType from '../Tree/Constants/FocusableNodeType';
import FocusableNode from '../Tree/FocusableNode';
import FocusWorker from '../Tree/FocusWorker';
import TreeWorker from '../Tree/TreeWorker';
import findParentOfType from '../Tree/Util/findParentOfType';

import Config from './Config';
import FocusManagerState from './FocusManagerState';

class FocusManager extends EventTarget<TFocusableEventType> {
    private state = new FocusManagerState();

    public get config(): Config {
        return this.state.config;
    }

    public get root(): FocusableNode | null {
        return this.state.root;
    }

    public get registeredNodes(): {[id: string]: FocusableNode} {
        return this.state.lookupTable;
    }

    public get focusedNode(): FocusableNode | null {
        return this.state.$$focusedNode;
    }

    /**
     * A setter for the `$$focusedNode` property. By proxying through this
     * setter when updating the focused nodes, a `BLUR` event is automatically
     * dispatched from any previously focused node, and a `FOCUS` event is
     * dispatched from the newly focused node.
     */

    public set focusedNode(nodeToFocus: FocusableNode | null) {
        const nodeToBlur = this.focusedNode;

        if (nodeToFocus === nodeToBlur) return;

        if (nodeToFocus && nodeToFocus.isFocusDisabled) return;

        this.state.$$focusedNode = nodeToFocus;

        if (nodeToFocus && nodeToFocus.parent !== nodeToBlur?.parent) {
            const {horizontalDelayMs, verticalDelayMs} = FocusManager.getNextThrottleConfig(
                nodeToFocus,
                this.config.throttleDurationMs
            );

            this.state.horizontalThrottleConfig.delay = horizontalDelayMs;
            this.state.verticalThrottleConfig.delay = verticalDelayMs;
        }

        if (nodeToBlur) {
            const blurEvent = new FocusableEvent(FocusableEventType.BLUR);

            nodeToBlur.dispatchEvent(blurEvent);
        }

        if (nodeToFocus) {
            const focusEvent = new FocusableEvent(FocusableEventType.FOCUS);

            nodeToFocus.dispatchEvent(focusEvent);
        }

        const eventDetailFocusChange: IEventDetailFocusChange = {
            from: nodeToBlur,
            to: nodeToFocus,
        };

        const focusChangeEvent = new FocusableEvent(FocusableEventType.FOCUS_CHANGE, {detail: eventDetailFocusChange});

        focusChangeEvent.target = this.root!;

        this.dispatchEvent(focusChangeEvent);
    }

    private throttledHandleHorizontalInput: (inputType: TInputType) => void;

    private throttledHandleVerticalInput: (inputType: TInputType) => void;

    constructor(options: Partial<Config>) {
        super();

        this.configure(options);

        this.handleClick = this.handleClick.bind(this);
        this.handleNativeBlur = this.handleNativeBlur.bind(this);
        this.handleNativeFocus = this.handleNativeFocus.bind(this);
        this.changeVirtualFocus = this.changeVirtualFocus.bind(this);
        this.registerFocusable = this.registerFocusable.bind(this);
        this.unregisterFocusable = this.unregisterFocusable.bind(this);
        this.reorderFocusableChildren = this.reorderFocusableChildren.bind(this);

        this.throttledHandleVerticalInput = throttle(this.handleInput.bind(this), this.state.verticalThrottleConfig);

        this.throttledHandleHorizontalInput = throttle(
            this.handleInput.bind(this),
            this.state.horizontalThrottleConfig
        );
    }

    public configure(options: Partial<Config>): void {
        if (process.env.NODE_ENV !== 'production') {
            if (!options.useNativeFocus) {
                if (options.focusOnHover) {
                    console.warn(Errors.INVALID_NATIVE_FOCUS_OPTION('focusOnHover'));
                }

                if (options.preventScroll) {
                    console.warn(Errors.INVALID_NATIVE_FOCUS_OPTION('preventScroll'));
                }

                if (options.selectOnClick) {
                    console.warn(Errors.INVALID_NATIVE_FOCUS_OPTION('selectOnClick'));
                }
            }
        }

        Object.assign(this.config, options);
    }

    public up(): void {
        this.throttledHandleVerticalInput(InputType.UP);
    }

    public down(): void {
        this.throttledHandleVerticalInput(InputType.DOWN);
    }

    public left(): void {
        this.throttledHandleHorizontalInput(InputType.LEFT);
    }

    public right(): void {
        this.throttledHandleHorizontalInput(InputType.RIGHT);
    }

    public select(): void {
        this.handleInput(InputType.SELECT);
    }

    public blur(): void {
        this.handleInput(InputType.BLUR);
    }

    /**
     * Programmatically applies focus to an arbitrary focusable node using its ID or
     * a path of indices from the root. If an ID and an empty path is passed, the first
     * focusable child of the node will be focused.
     *
     * Alternatively, both an ID of a node to descend from, *and* a path may be supplied.
     *
     * @param idOrPath A string ID or path of indices with which to target a focusable node.
     * @param optionalPath An optional path to descend from, if passing an ID as the
     *  first argument.
     */

    public focus(idOrPath: string | number[], optionalPath?: number[]): void {
        let id: string = '';
        let path: number[] | null = null;

        if (typeof idOrPath === 'string') {
            id = idOrPath;
        } else if (Array.isArray(idOrPath)) {
            path = idOrPath;
        } else {
            throw new TypeError(Errors.INVALID_FOCUS_ARGUMENT());
        }

        if (Array.isArray(optionalPath)) {
            path = optionalPath;
        }

        let nextFocusedNode: FocusableNode | null = this.state.root;

        if (!nextFocusedNode) {
            throw new Error(Errors.FOCUSABLE_CHILDREN_NOT_FOUND());
        }

        if (id) {
            nextFocusedNode = this.state.lookupTable[id];

            if (!nextFocusedNode) throw new Error(Errors.NO_NODE_FOUND(id));
        }

        // An empty path passed with an ID denotes a `FOCUS_CHILD` action.
        // Implemented as an inline function for type-narrowing purposes.

        const hasIdAndEmptyPath = (aPath: number[] | null): aPath is number[] =>
            Boolean(id && Array.isArray(aPath) && !aPath.length);

        if (isArrayWithLength(path) || hasIdAndEmptyPath(path)) {
            nextFocusedNode = FocusWorker.focusChild(nextFocusedNode, path);
        }

        this.focusedNode = nextFocusedNode;
    }

    /**
     * Allows a binding to provide a custom order that will override the order
     * that nodes were registered in for a container (e.g. using DOM measurement).
     */

    public reorderFocusableChildren(parentId: string, customOrder: FocusableNode[]): void {
        TreeWorker.reorderFocusableChildren(this.state.lookupTable[parentId], customOrder);
    }

    /**
     * Adds a focusable node to the tree and lookup table when its associated
     * component mounts.
     *
     * @param node The node to be registered.
     * @param focusOnRegistration
     *  An optional boolean dictating whether the node should be
     *  focused upon registration (component "mount").
     */

    public registerFocusable(node: FocusableNode, focusOnRegistration: boolean = false): void {
        if (process.env.NODE_ENV !== 'production') {
            if (this.config.useNativeFocus) {
                if (!node.hasFocusableElement) {
                    console.warn(Errors.FOCUSABLE_ELEMENT_NOT_PROVIDED(node.id));
                }
            } else if (node.hasFocusableElement && node.isLeaf && !this.config.useNativeFocus) {
                console.warn(Errors.NATIVE_FOCUS_MODE_NOT_SET());
            }
        }

        const root = TreeWorker.registerFocusable(this.state.lookupTable, this.state.parentLookupTable, node);

        if (root) {
            this.state.root = root;

            if (this.state.pendingIdToFocus) {
                this.focus(this.state.pendingIdToFocus, []);

                this.state.pendingIdToFocus = '';
            }
        }

        if (focusOnRegistration) {
            // If the newly registered node has requested focus, firstly
            // check if we have a root node or not.

            if (this.state.root) {
                // If we have a root, the tree is fully mounted and
                // interactable, the node can be immediately focused.

                this.focus(node.id, []);
            } else {
                // If we do not, we are in the process of mounting the
                // tree for the first time, and should wait for the root
                // to mount before focusing any child node.

                this.state.pendingIdToFocus = node.id;
            }
        }
    }

    /**
     * Removes a focusable node and all its children from the tree and lookup table
     * when its associated component unmounts.
     *
     * @param id The ID of the focusable component to be unregistered.
     */

    public unregisterFocusable(id: string): void {
        TreeWorker.unregisterFocusable(this.state.lookupTable, id);

        if (this.focusedNode && this.focusedNode.id !== id) return;

        Promise.resolve().then(() => {
            // If the unregistered node is still in focus after the
            // stack is clear, nullify it.

            if (this.focusedNode && id === this.focusedNode.id) this.focusedNode = null;
        });
    }

    /**
     * Handles native focus being dispatched from the focusable DOM element of
     * a focusable node, and sets it as the currently focused node.
     *
     * @param id The id of the focusable node containing the DOM element
     *  dispatching the blur event.
     */

    public handleNativeFocus(id: string): void {
        const nodeToFocus = this.state.lookupTable[id];

        this.focusedNode = nodeToFocus;
    }

    /**
     * Handles native blur being dispatched from the focusable DOM element of
     * a focusable node, and blurs the node.
     *
     * @param id The id of the focusable node containing the DOM element
     *  dispatching the blur event.
     */

    public handleNativeBlur(id: string): void {
        Promise.resolve().then(() => {
            // If the node that dispatched the native blur event is still
            // in focus after the stack is clear, blur it.
            // This avoids unecessary re-renders to blur and re-focus, while
            // transferring focus from one node to another.

            if (!this.focusedNode || this.focusedNode.id !== id) return;

            this.focusedNode = null;
        });
    }

    /**
     * Handles click events being dispatched from the focusable DOM element
     * of a focusable node.
     *
     * Mimics a `SELECT` input event being dispatching from the node, if
     * it is in focus.
     *
     * @param id The id of the focusable node containing the DOM element
     * dispatching the click event.
     */

    public handleClick(id: string): void {
        if (!this.focusedNode || id !== this.focusedNode.id) return;

        this.handleInput(InputType.SELECT);
    }

    /**
     * Triggers the final change of focus within a virtual container, after userland render
     * logic is complete.
     *
     * Triggered automatically before DOM update by default, or called by the consumer if a
     * `delegateChangeFocus` prop is passed to a virtual container.
     *
     * @param parentId The id of a virtual container.
     * @param virtualFocusedIndex The index of the next node to be focused, relative to
     *   an artificial rendered subset of the full dataset.
     */

    public changeVirtualFocus(parentId: string, virtualFocusedIndex: number): void {
        let index: number;

        if ((index = this.state.updatingVirtualContainers.indexOf(parentId)) < 0) return;

        this.state.updatingVirtualContainers.splice(index, 1);

        const nextFocusedNode = FocusWorker.getNextVirtualFocusedNode(
            this.state.lookupTable,
            parentId,
            virtualFocusedIndex
        );

        this.focusedNode = nextFocusedNode;
    }

    /**
     * Handles the calling of any input method on the public
     * `FocusManagerFacade` instance (typically in response to
     * a key event being handled).
     *
     * @param inputType The input method called.
     */

    private handleInput(inputType: TInputType): void {
        let event: FocusableEvent | null = null;
        let inputNode = this.focusedNode;

        if (inputNode) {
            switch (inputType) {
                case InputType.LEFT:
                    event = new FocusableEvent(FocusableEventType.LEFT);

                    break;
                case InputType.RIGHT:
                    event = new FocusableEvent(FocusableEventType.RIGHT);

                    break;
                case InputType.DOWN:
                    event = new FocusableEvent(FocusableEventType.DOWN);

                    break;
                case InputType.UP:
                    event = new FocusableEvent(FocusableEventType.UP);

                    break;
                case InputType.SELECT:
                    event = new FocusableEvent(FocusableEventType.SELECT);

                    break;
            }

            if (event) {
                inputNode.dispatchEvent(event);

                // If `event.preventDefault()` was invoked by the consumer on `LEFT/RIGHT/DOWN/UP`,
                // do not proceed with focus manipulation.

                if (event.defaultPrevented) return;
            }
        }

        // If we are within a virtual container, delegate the input handling as appropriate.

        let didVirtualContainerHandleInput: boolean = false;

        while (inputNode && inputNode.parent && !didVirtualContainerHandleInput) {
            // If not handled, but another container exists above,
            // propagate up and try again until we reach the root

            // NB: We cannot be sure that we are not within a virtual
            // container, until we have ascended through every level and
            // arrived at the root.

            // This means that an O(log n) ascension of the tree is required
            // on every input event, in order to determine whether an input
            // requires "virtual" handling, or "standard" handling.

            if (inputNode.parent.isVirtualContainer) {
                didVirtualContainerHandleInput = this.handleVirtualContainerInput(inputNode, inputType);
            }

            inputNode = inputNode.parent;
        }

        if (didVirtualContainerHandleInput) return;

        // If input was not handled by virtual container, treat as standard input handling.

        const [nextFocusedNode, nextFocusedAnchorIndex] = FocusWorker.getNextFocusedNode(
            this.focusedNode,
            this.state.root,
            this.state.lookupTable,
            this.state.focusedAnchorIndex,
            inputType
        );

        this.focusedNode = nextFocusedNode;
        this.state.focusedAnchorIndex = nextFocusedAnchorIndex;
    }

    /**
     * Manages the lifecycle of consumer input and userland virtualisation code for
     * "virtual" containers.
     *
     * @param inputNode The node from which the input was dispatched. This may be the
     *   focused node or a parent of it, depending on whether the input was propagated
     *   through multiple virtual containers.
     * @returns A boolean indicating if the input was handled within the virtualised
     *   realm or not.
     */

    private handleVirtualContainerInput(inputNode: FocusableNode, inputType: TInputType): boolean {
        const parent = inputNode.parent!;
        const actionType = inputNode.actions[inputType] as TActionType;

        // Calculate the next non-virtual focused index, relative to the entire
        // dataset

        const nextFocusedIndex = FocusWorker.getNextNonVirtualFocusedIndex(inputNode, actionType);

        // If `-1` (e.g, at parent boundary), return as "unhandled".

        if (nextFocusedIndex < 0) return false;

        // Else, dispatch event to userland to be handled as appropriate by custom
        // virtualisation logic

        const detail: IEventDetailFocusWillChange = {
            nextFocusedIndex,

            // Provide a userland hook to be invoked once all logic is complete, and the
            // virtualised list has been re-rendered, so that the we can finish up by changing
            // the focus, within the new virtualised set.

            changeFocus: () => this.changeVirtualFocus(parent.id, parent.virtualFocusedIndex),
        };

        const event = new FocusableEvent(FocusableEventType.FOCUS_WILL_CHANGE, {
            detail,
            canPropagate: false,
        });

        this.state.updatingVirtualContainers.push(parent.id);

        parent.dispatchEvent(event!);

        return true;
    }

    /**
     * Updates the horizontal and vertical throttling values based on the
     * configuration of the closest horizontal and vertical containers, or
     * falling back to the default provider config if not set.
     *
     * @param nodeToFocus The node in focus.
     */

    private static getNextThrottleConfig(
        nodeToFocus: FocusableNode,
        defaultThrottleDurationMs: number
    ): {
        horizontalDelayMs: number;
        verticalDelayMs: number;
    } {
        const closestVerticalParent = findParentOfType(nodeToFocus, [
            FocusableNodeType.VERTICAL_LIST,
            FocusableNodeType.VIRTUAL_VERTICAL_LIST,
            FocusableNodeType.GRID,
        ]);

        const closestHorizontalParent = findParentOfType(nodeToFocus, [
            FocusableNodeType.HORIZONTAL_LIST,
            FocusableNodeType.VIRTUAL_HORIZONTAL_LIST,
            FocusableNodeType.GRID,
        ]);

        return {
            horizontalDelayMs: closestHorizontalParent?.throttleDurationMs || defaultThrottleDurationMs,
            verticalDelayMs: closestVerticalParent?.throttleDurationMs || defaultThrottleDurationMs,
        };
    }
}

export default FocusManager;
